import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../styles/Home.css';

const Features = () => {
  const blueprintContainerRef = useRef(null);
  const waveContainerRef = useRef(null);
  const particlesContainerRef = useRef(null);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  useEffect(() => {
    // Create background circles with enhanced animation
    const createBackgroundCircles = () => {
      if (!waveContainerRef.current) return;
      
      const sizes = [300, 500, 700];
      const colors = ['rgba(100, 180, 255, 0.1)', 'rgba(100, 180, 255, 0.15)', 'rgba(100, 180, 255, 0.2)'];
      
      sizes.forEach((size, index) => {
        const circle = document.createElement('div');
        circle.className = 'hero-circle';
        circle.style.width = `${size}px`;
        circle.style.height = `${size}px`;
        circle.style.left = `calc(70% - ${size/2}px)`;
        circle.style.top = `calc(50% - ${size/2}px)`;
        circle.style.animationDelay = `${index * 1.5}s`;
        circle.style.backgroundColor = colors[index];
        circle.style.boxShadow = `0 0 ${size/2}px ${colors[index]}`;
        
        waveContainerRef.current.appendChild(circle);
      });
    };
    
    // Create floating particles
    const createParticles = () => {
      if (!particlesContainerRef.current) return;
      
      const particleCount = 50;
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'floating-particle';
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.top = `${Math.random() * 100}%`;
        particle.style.animationDelay = `${Math.random() * 5}s`;
        particle.style.animationDuration = `${5 + Math.random() * 10}s`;
        
        particlesContainerRef.current.appendChild(particle);
      }
    };
    
    // Create construction measurement lines with enhanced styling
    const createMeasurementLines = () => {
      if (!blueprintContainerRef.current) return;
      
      const measurementTypes = [
        { className: 'vertical-measure-1 measurement', length: '200px' },
        { className: 'vertical-measure-2 measurement', length: '300px' },
        { className: 'horizontal-measure-1 measurement', length: '250px' },
        { className: 'horizontal-measure-2 measurement', length: '350px' }
      ];
      
      measurementTypes.forEach((measure) => {
        const line = document.createElement('div');
        line.className = measure.className;
        line.style.height = measure.length;
        blueprintContainerRef.current.appendChild(line);
      });
      
      // Add construction tool icons with enhanced positioning
      const toolTypes = [
        { className: 'ruler construction-element', position: { top: '20%', left: '15%' } },
        { className: 'pencil construction-element', position: { top: '30%', right: '20%' } },
        { className: 'compass construction-element', position: { bottom: '25%', left: '25%' } },
        { className: 'hammer construction-element', position: { bottom: '35%', right: '15%' } },
        { className: 'helmet construction-element', position: { top: '45%', left: '40%' } }
      ];
      
      toolTypes.forEach((tool) => {
        const toolElement = document.createElement('div');
        toolElement.className = tool.className;
        toolElement.style.top = tool.position.top;
        toolElement.style.left = tool.position.left;
        toolElement.style.right = tool.position.right;
        toolElement.style.bottom = tool.position.bottom;
        blueprintContainerRef.current.appendChild(toolElement);
      });
    };
    
    createBackgroundCircles();
    createParticles();
    createMeasurementLines();
    
    return () => {
      if (blueprintContainerRef.current) {
        blueprintContainerRef.current.innerHTML = '';
      }
      if (waveContainerRef.current) {
        waveContainerRef.current.innerHTML = '';
      }
      if (particlesContainerRef.current) {
        particlesContainerRef.current.innerHTML = '';
      }
    };
  }, []);

  // Primary features with icons and details
  const primaryFeatures = [
    {
      id: 'Sales',
      icon: "✨",
      title: "On Site Sales",
      description: "Build, price, and present quotes right from the job site. Impress homeowners with instant, professional proposals—and turn more visits into signed deals.",
      benefits: [
        "Instant on-site quoting tools",
        "Accurate material and labor pricing",
        "Professional proposals in minutes",
        "Real-time customer approvals and e-signatures"
      ],
      imageSrc: "https://images.unsplash.com/photo-1586281380117-5a60ae2050cc?q=80&w=1470&auto=format&fit=crop"
    },
    {
      id: 'collaboration',
      icon: "👥",
      title: "Real-time Collaboration",
      description: "Work seamlessly with your team on presentations. Multiple team members can edit simultaneously with real-time syncing of changes.",
      benefits: [
        "Simultaneous editing with multiple users",
        "Real-time changes and updates",
        "Comment and feedback system",
        "Role-based permissions"
      ],
      imageSrc: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1470&auto=format&fit=crop"
    },
    {
      id: 'analytics',
      icon: "📊",
      title: "Reporting & Analytics",
      description: "Gain insights into your business with detailed reports. Track profitability, identify bottlenecks, and make data-driven decisions.",
      benefits: [
        "Comprehensive job and sales reports",
        "Real-time profit and cost tracking",
        "Performance insights by crew or project",
        "Data-driven dashboards for smarter decisions"
      ],
      imageSrc: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1470&auto=format&fit=crop"
    }
  ];

  // Secondary features in a grid
  const secondaryFeatures = [
    {
      id: 'ai-tools',
      icon: "🤖",
      title: "AI-Powered Tools",
      description: "Leverage the power of AI to create better presentations faster. Generate content, get design suggestions, and optimize your slides with a click.",
      benefits: [
        "AI-generated content suggestions",
        "Automatic slide formatting",
        "Design improvement recommendations",
        "Smart image enhancement"
      ],
      imageSrc: "https://images.unsplash.com/photo-1677442135205-39466cce0f8e?q=80&w=1470&auto=format&fit=crop"
    },
    {
      icon: "🔄",
      title: "Version Control",
      description: "Keep track of changes and revert to previous versions whenever needed."
    },
    {
      icon: "🌐",
      title: "Present Anywhere",
      description: "Present directly from the browser on any device, no downloads required."
    },
    {
      icon: "🎨",
      title: "Brand Management",
      description: "Ensure brand consistency with custom color palettes, fonts, and logos."
    },
    {
      icon: "🔒",
      title: "Secure Sharing",
      description: "Share presentations securely with password protection and access controls."
    },
    {
      icon: "📱",
      title: "Mobile Support",
      description: "Create and edit presentations on-the-go with our mobile-optimized interface."
    },
    {
      icon: "🔄",
      title: "Integrations",
      description: "Connect with your favorite tools like Slack, Google Drive, and Microsoft 365."
    }
  ];

  return (
    <div className="relative min-h-screen">
      {/* Background Elements */}
      <div ref={waveContainerRef} className="wave-container" />
      <div ref={blueprintContainerRef} className="blueprint-container" />
      <div ref={particlesContainerRef} className="particles-container" />
      
      <div className="pt-40 px-4 relative z-10">
        {/* Hero Section */}
        <motion.div 
          className="container mx-auto max-w-4xl text-center mb-16"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.h1 
            variants={fadeIn}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Comprehensive Decking Industry Solutions
          </motion.h1>
          <motion.p 
            variants={fadeIn}
            className="text-xl text-gray-600"
          >
            From real-time material costs to labor tracking, discover how our industry-specific tools help you maximize profitability in your decking business.
          </motion.p>
        </motion.div>

        {/* Main Features */}
        <div className="container mx-auto max-w-6xl">
          {primaryFeatures.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-24"
            >
              <div className={`space-y-6 ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                <div className="flex items-center space-x-4">
                  <span className="text-4xl">{feature.icon}</span>
                  <h2 className="text-3xl font-bold text-gray-900">{feature.title}</h2>
                </div>
                <p className="text-lg text-gray-600">{feature.description}</p>
                <ul className="space-y-3">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center">
                      <svg className="w-5 h-5 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`${index % 2 === 1 ? 'md:order-1' : ''}`}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  className="relative overflow-hidden rounded-lg shadow-xl"
                >
                  <img
                    src={feature.imageSrc}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Features; 
